import { getPayload as getAttributionPayload } from './attribution.js';

const FORM_READY_EVENT = 'hs-form-event:on-ready';
const CLICK_ID_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'ttclid',
    'li_fat_id'
];

function compact(values) {
    return Object.fromEntries(
        Object.entries(values).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
        )
    );
}

function getPropertyName(fieldName) {
    return String(fieldName || '')
        .split('/')
        .pop();
}

function getUtmValues(touch) {
    const landingPage = touch?.landingPage || touch?.landingPath;
    if (!landingPage) return {};

    try {
        const url = new URL(landingPage, 'https://www.stayavari.com');
        return compact({
            utm_source: url.searchParams.get('utm_source'),
            utm_medium: url.searchParams.get('utm_medium'),
            utm_campaign: url.searchParams.get('utm_campaign'),
            utm_content: url.searchParams.get('utm_content'),
            utm_term: url.searchParams.get('utm_term'),
            utm_id: url.searchParams.get('utm_id')
        });
    } catch {
        return {};
    }
}

function getPrimaryClickId(touch) {
    const clickIds = touch?.clickIds || {};
    const type = CLICK_ID_KEYS.find((key) => clickIds[key]);
    return type ? { type, value: clickIds[type] } : {};
}

function touchFields(prefix, touch) {
    const clickId = getPrimaryClickId(touch);

    return compact({
        [`${prefix}_source`]: touch?.source,
        [`${prefix}_medium`]: touch?.medium,
        [`${prefix}_campaign`]: touch?.campaign,
        [`${prefix}_content`]: touch?.content,
        [`${prefix}_term`]: touch?.term,
        [`${prefix}_landing_page`]: touch?.landingPage,
        [`${prefix}_referrer`]: touch?.referrer,
        [`${prefix}_click_id_type`]: clickId.type,
        [`${prefix}_click_id`]: clickId.value
    });
}

export function buildHubSpotAttributionFields(payload) {
    if (!payload || typeof payload !== 'object') return {};

    const firstTouch = payload.firstTouch || {};
    const lastTouch = payload.lastTouch || firstTouch;
    const activeTouch = lastTouch || firstTouch;
    const clickIds = activeTouch.clickIds || {};

    let snapshot = '';
    try {
        snapshot = JSON.stringify(payload);
    } catch {
        // The stored payload is plain data, but do not break forms if it is altered.
    }

    return compact({
        ...getUtmValues(activeTouch),
        ...Object.fromEntries(CLICK_ID_KEYS.map((key) => [key, clickIds[key]])),
        ...touchFields('first_touch', firstTouch),
        ...touchFields('last_touch', lastTouch),
        marketing_attribution_snapshot: snapshot
    });
}

export async function populateHubSpotAttributionFields(
    form,
    payload = getAttributionPayload()
) {
    if (!form?.getFormFieldValues || !form?.setFieldValue) return 0;

    try {
        const fieldValues = buildHubSpotAttributionFields(payload);
        const formFields = await form.getFormFieldValues();
        let populated = 0;

        for (const field of formFields || []) {
            const propertyName = getPropertyName(field?.name);
            const value = fieldValues[propertyName];
            if (!propertyName || value === undefined) continue;

            // These attribution properties are configured as hidden fields.
            // HubSpot Forms V4 requires string[] for hidden field values,
            // regardless of the value shape returned by getFormFieldValues().
            const nextValue = [String(value)];
            try {
                form.setFieldValue(field.name, nextValue);
                populated += 1;
            } catch {
                // A misconfigured field should not block the remaining values.
            }
        }

        return populated;
    } catch {
        // Attribution should never prevent HubSpot forms from rendering or submitting.
        return 0;
    }
}

export function initHubSpotAttribution() {
    const populate = (form) => {
        if (form) void populateHubSpotAttributionFields(form);
    };

    const onFormReady = (event) => {
        populate(window.HubSpotFormsV4?.getFormFromEvent?.(event));
    };

    window.addEventListener(FORM_READY_EVENT, onFormReady);

    // The app bundle is at the end of the page, so also cover forms that became
    // ready before this listener was registered.
    window.HubSpotFormsV4?.getForms?.().forEach(populate);
}
